import React from 'react';
import i18n from "i18next";
import { Button, Tooltip, Row, Col, Modal, Form, Input, message } from 'antd';
import { CloudSyncOutlined, FileAddOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import Search from '../../components/search/Search';
import ServerFilter from '../../components/namespace/ServerFilter';
import NamespaceRepository from '../../components/namespace/NamespaceRepository';
import SettingsAction from '../../redux/SettingsActions';
import { LettersCounter } from '../../components/alphabet/Letters';
import { FilterGroup } from '../../components/filter/FilterData';


const showAdd = (formRef, onFinishOperation, onCancelOperation) => (
  <Modal
    open={true}
    title="Добавление namespace prefix"
    cancelText="Отмена"
    okText="Добавить"
    onCancel={onCancelOperation}
    onOk={onFinishOperation}
    destroyOnHidden
  >
    <Form ref={formRef} layout="vertical">
      <Form.Item
        name="namespace"
        label="Имя namespace"
        rules={[{ required: true, whitespace: true, message: 'Укажите имя namespace.' }]}
      >
        <Input autoFocus placeholder="Например, dcat" />
      </Form.Item>
      <Form.Item
        name="uri"
        label="URI namespace"
        rules={[
          { required: true, whitespace: true, message: 'Укажите URI namespace.' },
          { type: 'url', message: 'Укажите корректный URI.' }
        ]}
      >
        <Input placeholder="https://www.w3.org/ns/dcat#" />
      </Form.Item>
    </Form>
  </Modal>
);

const getUriServer = (uri) => {
  if (typeof uri !== 'string' || !uri.trim()) return '';
  try {
    const url = new URL(uri.trim());
    return url.host || url.protocol;
  } catch {
    return 'invalid URI';
  }
};

const getPrefixFirstLetter = (namespace) => {
  const prefix = typeof namespace === 'string' ? namespace.trim() : '';
  return prefix ? Array.from(prefix)[0].toUpperCase() : '#';
};

const filterNamespaces = (namespaces, selectedServerUri, selectedLetter, selectedContent) => {
  const servers = new Set(selectedServerUri);
  const letters = new Set(selectedLetter.map(letter => letter.toUpperCase()));
  const content = selectedContent?.trim().toLowerCase() || "";

  return namespaces.filter(({ns, uri}) =>
    (!letters.size || letters.has(getPrefixFirstLetter(ns))) &&
    (!servers.size || servers.has(getUriServer(uri))) &&
    (
      !content ||
      ns?.toLowerCase().includes(content) ||
      uri?.toLowerCase().includes(content)
    )
  );
};

const makeConditions = (selectedServerUri, selectedLetter, selectedContent) => {
  const conditions = [
    ...selectedServerUri.map(serverUri  => ({label: serverUri, value: "uri"})),
    ...selectedLetter.map(letter  => ({label: letter, value: "letter"})),
  ]
  if (selectedContent?.length>0)
    conditions.push({label: selectedContent, value: "name"});
  return conditions;
};


const getNamespaceStatistics = (namespaces = []) => {
  const prefixCounts = new Map();
  const serverCounts = new Map();

  namespaces.forEach(({ns, uri}) => {
    const letter = getPrefixFirstLetter(ns);
    prefixCounts.set(letter, (prefixCounts.get(letter) || 0) + 1);

    const server = getUriServer(uri);
    if (server) serverCounts.set(server, (serverCounts.get(server) || 0) + 1);
  });

  return {
    prefixFirstLetters: [...prefixCounts]
      .map(([letter, count]) => ({letter, count}))
      .sort((a, b) => a.letter.localeCompare(b.letter)),
    uriServers: [...serverCounts]
      .map(([server, count]) => ({server, count}))
      .sort((a, b) => a.server.localeCompare(b.server))
  };
};

/**
 * Список пространств имён 
 */
class NamespaceList extends React.Component {
  state = {
    modal: 'none',
    selectedServerUri: [],
    selectedLetter: [],
    selectedContent: undefined,
  };

  formRef = React.createRef();

  onRefresh = (e) => {
    e.preventDefault();
    return this.props.onRefreshList(SettingsAction);
  };

  onNewNamespace = (e) => {
    e.preventDefault();
    this.setState({modal: 'add'});
  };

  cancelOperation = () => {
    this.formRef.current?.resetFields();
    this.setState({modal: 'none'});
  };

  onChangeServerUri = (serverUri) => {
    const hasInFilter = this.state.selectedServerUri.filter(uri => uri === serverUri).length>0;
    const selectedServerUri = hasInFilter
      ? this.state.selectedServerUri.filter(uri => uri !== serverUri)
      : [...this.state.selectedServerUri, serverUri];
    this.setState({selectedServerUri});
  }

  onChangeLetter = (letter) => {
    const hasInFilter = this.state.selectedLetter.filter(uri => uri === letter).length>0;
    const selectedLetter = hasInFilter
      ? this.state.selectedLetter.filter(uri => uri !== letter)
      : [... this.state.selectedLetter, letter];
    this.setState({selectedLetter});
  }


  onSearchByName (pattern) {
    if ("" === pattern || pattern === undefined || pattern === null)
      this.setState({selectedContent: undefined})
    else
      this.setState({selectedContent: pattern});
  }

  onChangeCondition = (include, condition) => {
    if (condition?.value === "uri"){
      this.onChangeServerUri(condition.label)
    };
    if (condition?.value === "letter"){
      this.onChangeLetter(condition.label)
    };
    if (condition?.value === "name"){
      this.setState({selectedContent: undefined})
    };
  }

  submitOperation = () => {
    const form = this.formRef.current;
    form.validateFields()
      .then(({namespace, uri}) => this.props.onAddNamespace(namespace.trim(), uri.trim()))
      .then(() => this.props.onRefreshList(SettingsAction))
      .then(() => {
        message.success('Namespace добавлен.');
        this.cancelOperation();
      })
      .catch(e => {
        if (e?.errorFields) return;
        message.error(`Ошибка добавления namespace. ${e}`);
      });
  };

  render() {
    const { modal, selectedServerUri, selectedLetter, selectedContent} = this.state;
    const { isSignedIn, inGroup, namespaces = [] } = this.props;
    const { prefixFirstLetters, uriServers } = getNamespaceStatistics(namespaces);
    const filteredNamespaces = filterNamespaces(namespaces, selectedServerUri, selectedLetter, selectedContent);
    const filteredNamespaceStatistics = getNamespaceStatistics(filteredNamespaces);
    const isCondition = selectedServerUri.length>0 || selectedLetter.length>0 || selectedContent?.length > 0;
    const conditions = isCondition ? makeConditions (selectedServerUri, selectedLetter, selectedContent) : [];  
    return (
      <div>
        <Row><h1>{i18n.t('views.admin.landing.namespaces')}</h1></Row>
        <Row>
          <Col className="csi_search_bar_title" span={6} style={{}}></Col>
          <Col className="csi_search_form_div" span={18} style={{}}><Search onSearch={(value) => this.onSearchByName(value)} /></Col>            
        </Row>
        <Row>
          <Col className="csi_filters" span={6} style={{}}>
            {isCondition && (
              <Row style={{}} className="filterBrowserDiv"> 
                <FilterGroup conditions={conditions} 
                  onChange = {(include, condition) => this.onChangeCondition(include, condition)}
                  reset={() => this.setState({selectedServerUri: [], selectedLetter: [], selectedContent: undefined})}/>
                </Row>
            )}
            <Row style={{}}> 
              <ServerFilter 
                uriServers={uriServers} selected={selectedServerUri} 
                onChange={(serverUri)=> this.onChangeServerUri(serverUri)} 
              />
            </Row>
          </Col>
          <Col className="csi_asset_list" span={18}>
            <Row gutter={16} style={{margin: '12px 0'}}>
              <Col span={24}>
                <LettersCounter 
                  letters={prefixFirstLetters} selected={selectedLetter} 
                  onChangeLetter={(letter) => this.onChangeLetter(letter)}
                  chars={filteredNamespaceStatistics.prefixFirstLetters.reduce((obj, { letter, count }) => { obj[letter] = count; return obj;}, {})} />
              </Col>
            </Row>
            <Row>
              <Col span={22}>
                <Tooltip placement="top" title={i18n.t('views.admin.return_admin_panel')}>
                  <Button icon={<ArrowLeftOutlined />} onClick={() => this.props.router.navigate('/')} />
                </Tooltip>
                &nbsp;&nbsp;
                <Tooltip placement="top" title={i18n.t('views.adms.list_reload_title')}>
                  <Button icon={<CloudSyncOutlined />} onClick={this.onRefresh} />
                </Tooltip>
              </Col>
              <Col span={2} style={{display: 'flex', flexDirection: 'row-reverse'}}>
                {isSignedIn && inGroup.Operator && (
                  <Tooltip placement="top" title="Добавить namespace">
                    <Button icon={<FileAddOutlined />} onClick={this.onNewNamespace} />
                  </Tooltip>
                )}
              </Col>
            </Row>
            <Row id="NamespaceRepository">
              <Col span={24}><NamespaceRepository {...this.props} namespaces={filteredNamespaces} /></Col>
            </Row>
          </Col>
        </Row>
        {modal === 'add' && showAdd(this.formRef, this.submitOperation, this.cancelOperation)}
      </div>
    );
  }
}

export default NamespaceList;